import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "sonner";
import api, { OWNER_CONTACT, waLink, mailLink } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Calendar } from "../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  ArrowLeft,
  BedDouble,
  Bath,
  Users,
  MapPin,
  Calendar as CalIcon,
  MessageCircle,
  Phone,
  Mail,
  Check,
} from "lucide-react";

export default function PropertyDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    guests: "",
    message: "",
  });
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api
      .get(`/properties/${id}`)
      .then((r) => setProperty(r.data))
      .catch(() => setProperty(null))
      .finally(() => setLoading(false));
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/inquiries", {
        property_id: property.id,
        name: form.name,
        email: form.email,
        phone: form.phone,
        check_in: checkIn ? format(checkIn, "yyyy-MM-dd") : "",
        check_out: checkOut ? format(checkOut, "yyyy-MM-dd") : "",
        guests: form.guests ? parseInt(form.guests, 10) : null,
        message: form.message,
      });
      toast.success("Inquiry sent! I'll reply within a few hours.");
      setForm({ name: "", email: "", phone: "", guests: "", message: "" });
      setCheckIn(null);
      setCheckOut(null);
    } catch (err) {
      toast.error("Could not send inquiry. Please try WhatsApp or email.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading)
    return (
      <div className="pt-40 text-center text-[#A3A3A3]" data-testid="property-loading">
        Loading…
      </div>
    );
  if (!property)
    return (
      <div className="pt-40 text-center" data-testid="property-not-found">
        <p className="font-serif-luxe text-4xl">Property not found</p>
        <Link
          to="/properties"
          className="mt-6 inline-block text-[#D4AF37] link-underline"
        >
          Back to the collection
        </Link>
      </div>
    );

  const waMsg = `Hi! I'm interested in ${property.name} at vacaystayrentals.com. Could you share availability?`;

  return (
    <div data-testid="property-detail-page" className="pt-28 pb-24">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <button
          onClick={() => nav(-1)}
          data-testid="back-btn"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#A3A3A3] hover:text-[#D4AF37] mb-8"
        >
          <ArrowLeft size={14} /> Back
        </button>

        {/* Gallery + Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8">
            <div className="aspect-[16/10] overflow-hidden bg-[#1C1C1E]">
              <img
                src={property.images?.[activeImg]}
                alt={property.name}
                className="w-full h-full object-cover"
                data-testid="property-main-image"
              />
            </div>
            {property.images?.length > 1 && (
              <div className="mt-3 grid grid-cols-4 gap-3">
                {property.images.map((src, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    data-testid={`thumb-${i}`}
                    className={`aspect-[4/3] overflow-hidden border ${
                      activeImg === i
                        ? "border-[#D4AF37]"
                        : "border-[#F5F5DC]/10 hover:border-[#F5F5DC]/30"
                    } transition-colors`}
                  >
                    <img
                      src={src}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="lg:col-span-4">
            <p className="overline text-[#D4AF37]">{property.location}</p>
            <h1
              className="font-serif-luxe text-5xl md:text-6xl leading-none tracking-tighter mt-3"
              data-testid="property-name"
            >
              {property.name}
            </h1>
            <p className="mt-4 text-[#F5F5DC]/80">{property.tagline}</p>

            <div className="mt-8 py-6 border-t border-b border-[#F5F5DC]/10 flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3]">
                  From
                </p>
                <p className="font-serif-luxe text-4xl text-[#D4AF37]">
                  ${property.price_per_night?.toLocaleString?.() || property.price_per_night}
                  <span className="text-sm text-[#737373]"> / night</span>
                </p>
              </div>
              <div className="flex items-center gap-1 text-xs">
                <MapPin size={14} className="text-[#D4AF37]" /> {property.location.split(",")[0]}
              </div>
            </div>

            <div className="mt-6 grid grid-cols-3 gap-4 text-center">
              <SpecTile icon={<BedDouble size={16} />} label="Bedrooms" value={property.bedrooms} />
              <SpecTile icon={<Bath size={16} />} label="Baths" value={property.bathrooms} />
              <SpecTile icon={<Users size={16} />} label="Sleeps" value={property.max_guests} />
            </div>

            <div className="mt-8 space-y-3">
              <a
                href={waLink(waMsg)}
                target="_blank"
                rel="noreferrer"
                data-testid="whatsapp-btn"
                className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#D4AF37] text-[#121212] text-xs tracking-[0.24em] uppercase hover:bg-[#B3922E] transition-all hover:translate-y-[-2px]"
              >
                <MessageCircle size={16} /> WhatsApp the host
              </a>
              <div className="grid grid-cols-2 gap-3">
                <a
                  href={`tel:+${OWNER_CONTACT.whatsapp}`}
                  data-testid="call-btn"
                  className="flex items-center justify-center gap-2 px-4 py-3 border border-[#F5F5DC]/20 hover:border-[#D4AF37] text-xs uppercase tracking-wider transition-all"
                >
                  <Phone size={14} /> Call
                </a>
                <a
                  href={mailLink(`Inquiry: ${property.name}`)}
                  data-testid="email-btn"
                  className="flex items-center justify-center gap-2 px-4 py-3 border border-[#F5F5DC]/20 hover:border-[#D4AF37] text-xs uppercase tracking-wider transition-all"
                >
                  <Mail size={14} /> Email
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Description + Amenities */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mt-24">
          <div className="lg:col-span-7">
            <p className="overline mb-4">About this home</p>
            <h2 className="font-serif-luxe text-4xl leading-tight">
              A place designed for the way you actually want to travel.
            </h2>
            <p className="mt-6 text-lg text-[#F5F5DC]/80 leading-relaxed whitespace-pre-line">
              {property.description}
            </p>
          </div>
          <div className="lg:col-span-4 lg:col-start-9">
            <p className="overline mb-4">Amenities</p>
            <ul className="space-y-3">
              {property.amenities?.map((a) => (
                <li
                  key={a}
                  className="flex items-center gap-3 text-[#F5F5DC]/90 border-b border-[#F5F5DC]/5 pb-3"
                >
                  <Check size={16} className="text-[#D4AF37]" />
                  {a}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Inquiry form */}
        <div
          data-testid="inquiry-section"
          className="mt-24 border border-[#F5F5DC]/10 bg-[#1C1C1E] p-8 md:p-12"
        >
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-5">
              <p className="overline mb-4">Send an inquiry</p>
              <h3 className="font-serif-luxe text-4xl md:text-5xl leading-none">
                Let's find your <span className="italic text-[#D4AF37]">dates</span>.
              </h3>
              <p className="mt-6 text-[#A3A3A3]">
                Fill in the form and I'll come back to you personally, usually
                within a few hours.
              </p>
            </div>
            <form
              className="md:col-span-7 space-y-5"
              onSubmit={submit}
              data-testid="inquiry-form"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FieldWrap label="Full name">
                  <Input
                    data-testid="inquiry-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="bg-[#121212] border-[#F5F5DC]/10 focus:border-[#D4AF37]"
                    required
                  />
                </FieldWrap>
                <FieldWrap label="Email">
                  <Input
                    data-testid="inquiry-email"
                    type="email"
                    value={form.email}
                    onChange={(e) =>
                      setForm({ ...form, email: e.target.value })
                    }
                    className="bg-[#121212] border-[#F5F5DC]/10 focus:border-[#D4AF37]"
                    required
                  />
                </FieldWrap>
                <FieldWrap label="Phone">
                  <Input
                    data-testid="inquiry-phone"
                    value={form.phone}
                    onChange={(e) =>
                      setForm({ ...form, phone: e.target.value })
                    }
                    className="bg-[#121212] border-[#F5F5DC]/10 focus:border-[#D4AF37]"
                  />
                </FieldWrap>
                <FieldWrap label="Guests">
                  <Input
                    data-testid="inquiry-guests"
                    type="number"
                    min={1}
                    value={form.guests}
                    onChange={(e) =>
                      setForm({ ...form, guests: e.target.value })
                    }
                    className="bg-[#121212] border-[#F5F5DC]/10 focus:border-[#D4AF37]"
                  />
                </FieldWrap>
                <DateField
                  label="Check-in"
                  value={checkIn}
                  onChange={setCheckIn}
                  testid="inquiry-checkin"
                />
                <DateField
                  label="Check-out"
                  value={checkOut}
                  onChange={setCheckOut}
                  testid="inquiry-checkout"
                  minDate={checkIn}
                />
              </div>
              <FieldWrap label="Message">
                <Textarea
                  data-testid="inquiry-message"
                  rows={4}
                  value={form.message}
                  onChange={(e) =>
                    setForm({ ...form, message: e.target.value })
                  }
                  className="bg-[#121212] border-[#F5F5DC]/10 focus:border-[#D4AF37]"
                  required
                />
              </FieldWrap>

              <Button
                type="submit"
                disabled={submitting}
                data-testid="inquiry-submit"
                className="w-full h-12 bg-[#D4AF37] text-[#121212] hover:bg-[#B3922E] text-xs tracking-[0.24em] uppercase rounded-none"
              >
                {submitting ? "Sending…" : "Send Inquiry"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

function SpecTile({ icon, label, value }) {
  return (
    <div className="border border-[#F5F5DC]/10 p-3">
      <div className="flex items-center justify-center text-[#D4AF37] mb-1">
        {icon}
      </div>
      <div className="text-lg font-serif-luxe text-[#F5F5DC]">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#737373]">
        {label}
      </div>
    </div>
  );
}

function FieldWrap({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3]">
        {label}
      </Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function DateField({ label, value, onChange, testid, minDate }) {
  return (
    <FieldWrap label={label}>
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={testid}
            className="w-full flex items-center justify-between h-10 px-3 bg-[#121212] border border-[#F5F5DC]/10 hover:border-[#D4AF37] text-sm text-[#F5F5DC]"
          >
            <span className={value ? "" : "text-[#737373]"}>
              {value ? format(value, "MMM d, yyyy") : "Select date"}
            </span>
            <CalIcon size={14} className="text-[#D4AF37]" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="p-0 bg-[#1C1C1E] border-[#F5F5DC]/10"
          align="start"
        >
          <Calendar
            mode="single"
            selected={value}
            onSelect={onChange}
            disabled={(date) =>
              date < new Date(new Date().setHours(0, 0, 0, 0)) ||
              (minDate && date <= minDate)
            }
          />
        </PopoverContent>
      </Popover>
    </FieldWrap>
  );
}
